// Steps/VolumeStep.tsx
import React from "react";
import { Field, useFormikContext } from "formik";
import { Form, Card, Row, Col } from "react-bootstrap";
import Select from "react-select";

const DOCUMENT_VOLUMES_ONETIME = [
  { value: "1-50", label: "1-50 documents" },
  { value: "51-200", label: "51-200 documents" },
  { value: "201-500", label: "201-500 documents" },
  { value: "501-1000", label: "501-1,000 documents" },
  { value: "1000+", label: "1,000+ documents" },
];

const DOCUMENT_VOLUMES_MONTHLY = [
  { value: "1-50", label: "1-50 documents per month" },
  { value: "51-200", label: "51-200 documents per month" },
  { value: "201-500", label: "201-500 documents per month" },
  { value: "501-1000", label: "501-1,000 documents per month" },
  { value: "1000+", label: "1,000+ documents per month" },
];

// Use case types for plan recommendations
const USE_CASE_TYPES = [
  {
    value: "one-off",
    title: "One-Time Project",
    description: "I have a specific project that needs to be completed once",
    icon: "📄",
    recommended: "Monthly billing (can be canceled at any time)",
  },
  {
    value: "ongoing",
    title: "Ongoing Need",
    description: "Regular document processing as part of ongoing operations",
    icon: "🔄",
    recommended: "Annual billing (save 30% - that's 4 months free!)",
  },
];

export const VolumeStep: React.FC = () => {
  const { values } = useFormikContext<any>();

  return (
    <div className="py-4">
      <Form.Group className="mb-4">
        <Form.Label className="h5 mb-3">
          What best describes your needs?
        </Form.Label>
        <p className="text-muted mb-4">
          We'll recommend the right plan based on your selection
        </p>
        <Field name="use_case_type">
          {({ field, form: { touched, errors, setFieldValue } }) => (
            <>
              <Row>
                {USE_CASE_TYPES.map((useCase) => (
                  <Col md={12} key={useCase.value} className="mb-3">
                    <Card
                      className={`use-case-card ${
                        field.value === useCase.value
                          ? "border-primary bg-light"
                          : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        transition: "all 0.2s",
                        borderWidth:
                          field.value === useCase.value ? "2px" : "1px",
                      }}
                      onClick={() => {
                        setFieldValue(field.name, useCase.value);
                        // Clear volume when switching use case type
                        setFieldValue("document_volume", "");
                        // Set frequency based on use case
                        if (useCase.value === "one-off") {
                          setFieldValue("document_frequency", "one-time");
                        } else if (useCase.value === "ongoing") {
                          setFieldValue("document_frequency", "recurring");
                        }
                      }}
                    >
                      <Card.Body>
                        <div className="d-flex align-items-start">
                          <div
                            className="mx-3"
                            style={{ fontSize: "32px", lineHeight: 1 }}
                          >
                            {useCase.icon}
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <div className="d-flex align-items-center">
                                <Form.Check
                                  type="radio"
                                  id={`use-case-${useCase.value}`}
                                  checked={field.value === useCase.value}
                                  onChange={() => {
                                    setFieldValue(field.name, useCase.value);
                                    // Clear volume when switching use case type
                                    setFieldValue("document_volume", "");
                                    // Set frequency based on use case
                                    if (useCase.value === "one-off") {
                                      setFieldValue(
                                        "document_frequency",
                                        "one-time"
                                      );
                                    } else if (useCase.value === "ongoing") {
                                      setFieldValue(
                                        "document_frequency",
                                        "recurring"
                                      );
                                    }
                                  }}
                                  className="mx-2"
                                />
                                <h5 className="mb-0">{useCase.title}</h5>
                              </div>
                            </div>
                            <p className="text-muted mb-2">
                              {useCase.description}
                            </p>
                            <small className="text-success">
                              <strong>✓ Recommended:</strong>{" "}
                              {useCase.recommended}
                            </small>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
              {touched.use_case_type &&
                errors.use_case_type &&
                !field.value && (
                  <div className="invalid-feedback d-block">
                    {errors.use_case_type}
                  </div>
                )}
            </>
          )}
        </Field>
      </Form.Group>

      {/* Show volume selector for one-time projects - DISABLED */}
      {false && values.use_case_type === "one-off" && (
        <Form.Group className="mb-4">
          <Form.Label className="h5">
            How many documents do you need to process?
          </Form.Label>
          <p className="text-muted mb-3">
            This helps us recommend the right pricing option
          </p>
          <Field name="document_volume">
            {({ field, form: { touched, errors, setFieldValue } }) => (
              <>
                <Select
                  options={DOCUMENT_VOLUMES_ONETIME}
                  value={DOCUMENT_VOLUMES_ONETIME.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(option) =>
                    setFieldValue(field.name, option?.value)
                  }
                  isClearable
                  placeholder="Select document volume..."
                  className={`form-select-container ${
                    touched.document_volume && errors.document_volume
                      ? "is-invalid"
                      : ""
                  }`}
                  classNamePrefix="form-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor:
                        touched.document_volume && errors.document_volume
                          ? "#dc3545"
                          : base.borderColor,
                      padding: "4px",
                      fontSize: "16px",
                    }),
                  }}
                />
                {touched.document_volume && errors.document_volume && (
                  <div className="invalid-feedback d-block">
                    {errors.document_volume}
                  </div>
                )}
              </>
            )}
          </Field>
        </Form.Group>
      )}

      {/* Show volume selector for ongoing needs - DISABLED */}
      {false && values.use_case_type === "ongoing" && (
        <Form.Group className="mb-4">
          <Form.Label className="h5">
            What's your typical monthly volume?
          </Form.Label>
          <p className="text-muted mb-3">
            This helps us recommend the right plan tier
          </p>
          <Field name="document_volume">
            {({ field, form: { touched, errors, setFieldValue } }) => (
              <>
                <Select
                  options={DOCUMENT_VOLUMES_MONTHLY}
                  value={DOCUMENT_VOLUMES_MONTHLY.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(option) =>
                    setFieldValue(field.name, option?.value)
                  }
                  isClearable
                  placeholder="Select typical monthly volume..."
                  className={`form-select-container ${
                    touched.document_volume && errors.document_volume
                      ? "is-invalid"
                      : ""
                  }`}
                  classNamePrefix="form-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor:
                        touched.document_volume && errors.document_volume
                          ? "#dc3545"
                          : base.borderColor,
                      padding: "4px",
                      fontSize: "16px",
                    }),
                  }}
                />
                {touched.document_volume && errors.document_volume && (
                  <div className="invalid-feedback d-block">
                    {errors.document_volume}
                  </div>
                )}
              </>
            )}
          </Field>
        </Form.Group>
      )}
    </div>
  );
};
